/**
 * driverPoolSlice — Vivier RH dynamique (Phase R1+R2+R3, 04/02/2026).
 *
 * Demande utilisateur :
 *  1. Vivier mouvant — les profils ne restent pas indéfiniment.
 *  2. Concurrents recrutent dans le même vivier que nous.
 *  3. Débauchage uniquement avec chasseur de tête (Karim Sefrioui).
 *  4. Boosters d'apparition : RRH, site web, salon RH, cooptation,
 *     annonce site, candidatures spontanées.
 *
 * Modèle :
 *  - state.driverPool : {
 *       availableIds: string[]    // ids disponibles dans le vivier
 *       takenBy: { id: string }   // chauffeur pris par concurrent X
 *       lastTickDay: number       // dernier jour de rotation
 *       lastJobFairDay: number    // cooldown salon RH
 *       jobAdActive: boolean      // annonce site déposée ?
 *       jobAdEndDay: number       // expiration de l'annonce
 *     }
 *  - state.driverPoolEvents : tableau des événements affichés en UI
 *    (« 3 nouveaux profils cette semaine », « TransNord a recruté X »…)
 *
 * Le tick se déclenche tous les 7 jours via tickDriverPool().
 */
import { DRIVERS, HERO_DRIVERS, MARKET_DRIVERS } from '../../data/drivers';
import { isDriverUnlocked } from '../../utils/driverRecruitment';

/* ──────────────────────────── Constantes ──────────────────────────────── */
const TICK_INTERVAL_DAYS = 7;             // cadence de rotation
const BASE_POOL_VISIBLE = 6;              // vivier visible sans support RH
const VISIBLE_BONUS_RH_TIER_1 = 5;        // +5 avec Nadia Cheikh
const VISIBLE_BONUS_RH_TIER_2 = 5;        // +5 avec Karim Sefrioui
const VISIBLE_BONUS_TECH_WEBSITE = 3;     // +3 avec site web
const VISIBLE_BONUS_JOB_AD = 4;           // +4 si annonce active
const JOB_FAIR_COST = 5000;
const JOB_FAIR_COOLDOWN_DAYS = 30;
const JOB_FAIR_PROFILES_MIN = 5;
const JOB_FAIR_PROFILES_MAX = 8;
const JOB_AD_DURATION_DAYS = 14;
const COMPETITOR_POACH_PROB = 0.05;       // 5 % chance/tick qu'un concurrent prenne 1 profil
const MARKET_DEPART_PROB = 0.20;          // 20 % chance qu'un profil marché « trouve ailleurs »
const MARKET_ARRIVAL_PROB = 0.35;         // 35 % chance qu'un nouveau profil arrive
// 21/09/2026 — retour user prod « on ne croise plus les frères Lumery » :
// les héros niveau 4-5 n'entraient jamais dans le vivier (init ≤ N3, tick =
// profils marché uniquement). Avec Karim Sefrioui recruté, 25 % de chance
// par tick hebdo de faire entrer un profil élite manquant (en tête, visible).
const HEADHUNTER_ELITE_ARRIVAL_PROB = 0.25;
const HEADHUNTER_ELITE_MIN_LEVEL = 4;
// 22/07/2026 — retour user prod : « Ces frais n'existent pas en réalité,
// il faut amener une offre intéressante au chauffeur. » Le débauchage ne
// coûte plus 1.5× le salaire annuel : Karim Sefrioui approche le chauffeur
// gratuitement (son salaire mensuel couvre la prestation), mais le
// chauffeur — déjà en poste — n'acceptera qu'une offre salariale
// attractive : +15 % sur son salaire attendu (appliqué dans la négociation
// RH via driverNegotiations[id].poachPremium).
const POACH_SALARY_PREMIUM = 0.15;        // +15 % attendu à l'embauche
const POACH_REFUSAL_RATE = 0.35;          // 35 % de refus (loyauté)
const POACH_RETRY_COOLDOWN_DAYS = 45;     // nouvel essai après refus
const COMPETITORS_NAMES = [
  'TransNord Express',
  'LogistikMéridien',
  'Pyrénées Cargo',
  'Atlantique Routier',
];

/* ──────────────────────────── Utilitaires ────────────────────────────── */
function rng(seed) {
  // Mulberry32 — déterministe par seed
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pickRandom(arr, rand) {
  if (arr.length === 0) return null;
  return arr[Math.floor(rand() * arr.length)];
}

/** Calcule le nombre max de profils visibles pour le joueur. */
export function computeVisibleSlots(state) {
  let slots = BASE_POOL_VISIBLE;
  const hired = state.hiredSupport || {};
  if (hired['role-nadia-cheikh']) slots += VISIBLE_BONUS_RH_TIER_1;
  if (hired['role-karim-sefrioui']) slots += VISIBLE_BONUS_RH_TIER_2;
  const inv = state.activeInvestments || {};
  if (inv['inv-tech-02']) slots += VISIBLE_BONUS_TECH_WEBSITE;
  if (state.driverPool?.jobAdActive && state.totalDays < (state.driverPool.jobAdEndDay || 0)) {
    slots += VISIBLE_BONUS_JOB_AD;
  }
  return slots;
}

/* ──────────────────────────── Slice ──────────────────────────────────── */
export const createDriverPoolSlice = (set, get) => ({
  driverPool: {
    availableIds: [], // computed à l'initialisation, voir initDriverPool
    takenBy: {},
    lastTickDay: 0,
    lastJobFairDay: -999,
    jobAdActive: false,
    jobAdEndDay: 0,
  },
  driverPoolEvents: [],

  /** Initialise le vivier avec un mix RANDOM de héros + qq profils marché.
   *
   * 09/02/2026 — Q2 Lot F user prod : avant cette refonte, on prenait
   * TOUJOURS les 6 premiers héros (ordre du fichier drivers.js), ce qui
   * donnait la même équipe de départ à chaque nouvelle partie. User feedback :
   * « il faut que ça soit également plus vivant dans le renouvellement
   * des profils proposés ». Désormais :
   *   - 4 héros random tirés parmi les héros niveau 1-2 (early game viable)
   *   - 2 héros random tirés parmi les niveaux 1-3 toutes catégories
   *   - 2 market drivers Niveau 1-2 (le tickDriverPool ajoutera les autres
   *     au fil de l'eau).
   * Ainsi chaque partie démarre avec un mix unique de 6-8 profils.
   */
  initDriverPool: () => {
    const state = get();
    if (state.driverPool?.availableIds?.length > 0) return; // déjà init
    const seed = state.totalDays || 0;
    // Note : Math.random() est utilisé volontairement pour avoir un mix
    // vraiment différent à chaque nouvelle partie (pas de seed fixe).
    const shuffle = (arr) => arr.map(x => [Math.random(), x]).sort((a, b) => a[0] - b[0]).map(([, x]) => x);
    const earlyHeroes = HERO_DRIVERS.filter(d => (d.level || 1) <= 2);
    const midHeroes = HERO_DRIVERS.filter(d => (d.level || 1) <= 3);
    const earlyMarket = MARKET_DRIVERS.filter(d => (d.level || 1) <= 2);

    // 18/06/2026 — user prod : « il faut que dans le panel de base des
    // premiers chauffeurs recrutables, il y ait des chauffeurs avec
    // chacune des différentes possibilités (Frigo / Vrac etc.) ».
    // On garantit la couverture des spécialités accessibles en début de
    // partie : 1 profil aléatoire (niveau ≤ 2) par spécialité, placés EN
    // TÊTE de liste (les 6 premiers ids sont les slots visibles). ADR
    // reste hors panel initial (aucun profil < niveau 4 — certification
    // avancée débloquée par la progression).
    const CORE_SPECIALTIES = [
      'Messagerie/Colis', 'Vrac/Palettes', 'Frigo/Pharma',
      'BTP/Matériaux/Engins TP', 'Export UE',
    ];
    const covers = (d, spec) => d.specialty === spec || d.specialty2 === spec;
    // 13/07/2026 — user prod : « au début d'une partie il faut que le
    // vivier minimal soit de 3 chauffeurs [recrutables immédiatement] dans
    // le cas où un apprenant échoue dans sa négociation avec un ». Une
    // négociation ratée verrouille le candidat 45-90 j : il faut donc ≥ 3
    // profils DÉVERROUILLÉS à J0 dans les slots visibles. On privilégie
    // les candidats immédiatement recrutables à chaque tirage de
    // couverture (tri stable : le mélange aléatoire est conservé au sein
    // de chaque groupe).
    const dayZeroCtx = { vehicles: [], monthlyRevenues: 0, totalDays: 0 };
    const unlockedAtStart = (d) => isDriverUnlocked(d, dayZeroCtx);
    const MIN_STARTER_UNLOCKED = 3;
    const picks = new Set();
    const coveredSpecs = new Set();
    CORE_SPECIALTIES.forEach(spec => {
      if (coveredSpecs.has(spec)) return;
      const candidates = shuffle(earlyHeroes.concat(earlyMarket))
        .filter(d => !picks.has(d.id) && covers(d, spec))
        .sort((a, b) => (unlockedAtStart(b) ? 1 : 0) - (unlockedAtStart(a) ? 1 : 0));
      if (candidates.length > 0) {
        picks.add(candidates[0].id);
        [candidates[0].specialty, candidates[0].specialty2]
          .filter(Boolean).forEach(s => coveredSpecs.add(s));
      }
    });
    // Complément : héros mid-level + profils marché pour atteindre 6-8.
    shuffle(midHeroes).filter(d => !picks.has(d.id)).slice(0, 2).forEach(d => picks.add(d.id));
    shuffle(earlyMarket).filter(d => !picks.has(d.id))
      .slice(0, Math.max(0, 8 - picks.size)).forEach(d => picks.add(d.id));
    // Fallback minimal si tout le filtrage retourne <4 ids (cas extrêmes
    // dans un test ou seed déterministe). On tombe sur les 6 premiers
    // héros comme avant pour ne PAS casser les tests anciens.
    let availableIds = Array.from(picks);
    if (availableIds.length < 4) {
      availableIds = HERO_DRIVERS.slice(0, 6).map(d => d.id);
    }
    // Filet de sécurité : garantit ≥ 3 déverrouillés parmi les 6 slots
    // visibles (réordonne déverrouillés en tête, puis injecte des profils
    // 'Départ' supplémentaires si besoin).
    const ALL_POOL = HERO_DRIVERS.concat(MARKET_DRIVERS);
    const isIdUnlocked = (id) => {
      const d = ALL_POOL.find(x => x.id === id);
      return d ? unlockedAtStart(d) : false;
    };
    if (availableIds.slice(0, 6).filter(isIdUnlocked).length < MIN_STARTER_UNLOCKED) {
      const unlockedIds = availableIds.filter(isIdUnlocked);
      const lockedIds = availableIds.filter(id => !isIdUnlocked(id));
      const missing = MIN_STARTER_UNLOCKED - unlockedIds.length;
      if (missing > 0) {
        shuffle(earlyHeroes.concat(earlyMarket))
          .filter(d => !availableIds.includes(d.id) && unlockedAtStart(d))
          .slice(0, missing)
          .forEach(d => unlockedIds.push(d.id));
      }
      availableIds = [...unlockedIds, ...lockedIds];
    }
    set({
      driverPool: {
        ...state.driverPool,
        availableIds,
        lastTickDay: seed,
      },
    });
  },

  /**
   * Tick hebdomadaire — appelé depuis dailyTick.
   * Gère arrivées/départs aléatoires + poaching par concurrents.
   * Retourne true si le vivier a changé (déclencher un message inbox).
   */
  tickDriverPool: () => {
    const state = get();
    const pool = state.driverPool || {};
    const today = state.totalDays || 0;
    if (today - (pool.lastTickDay || 0) < TICK_INTERVAL_DAYS) return false;

    const rand = rng(today * 31 + 7);
    let availableIds = [...(pool.availableIds || [])];
    const takenBy = { ...(pool.takenBy || {}) };
    const events = [];

    // 1. Concurrents recrutent — un par tick avec proba.
    if (rand() < COMPETITOR_POACH_PROB && availableIds.length > 0) {
      const target = pickRandom(availableIds.filter(id => !takenBy[id]), rand);
      if (target) {
        const competitor = pickRandom(COMPETITORS_NAMES, rand);
        takenBy[target] = competitor;
        const dName = DRIVERS.find(d => d.id === target)?.name || target;
        events.push({ kind: 'poached', driverName: dName, competitor });
      }
    }

    // 2. Départs marché — un profil marché trouve ailleurs.
    const marketAvail = availableIds.filter(id => {
      const d = DRIVERS.find(x => x.id === id);
      return d?.archetype === 'market' && !takenBy[id];
    });
    if (rand() < MARKET_DEPART_PROB && marketAvail.length > 0) {
      const leaver = pickRandom(marketAvail, rand);
      availableIds = availableIds.filter(id => id !== leaver);
      const dName = DRIVERS.find(d => d.id === leaver)?.name || leaver;
      events.push({ kind: 'left', driverName: dName });
    }

    // 3. Arrivées marché — un nouveau profil rejoint le vivier.
    // 22/07/2026 — anti-doublon (retour user M&A « Hélène Vasseur ») : ne
    // jamais faire (re)venir au marché un visage déjà dans l'effectif
    // (embauché OU repris via un rachat de concurrent → même dataId).
    const _rosterIds = new Set(
      (state.drivers || []).flatMap(d => [d.id, d.dataId].filter(Boolean))
    );
    const candidates = MARKET_DRIVERS.filter(d => !availableIds.includes(d.id) && !_rosterIds.has(d.id));
    if (rand() < MARKET_ARRIVAL_PROB && candidates.length > 0) {
      const arriver = pickRandom(candidates, rand);
      availableIds.push(arriver.id);
      events.push({ kind: 'arrived', driverName: arriver.name });
    }

    // 4. 05/02/2026 — Q1 retour user prod : COOPTATION (chauffeur hired
    // satisfait recommande un profil de son réseau). 8 % chance/tick si
    // au moins 3 chauffeurs avec satisfaction > 70 + ancienneté > 30 j.
    const hiredDrivers = (state.drivers || []).filter(
      d => (d.satisfaction || 0) > 70 && ((state.totalDays || 0) - (d.hiredAt || 0)) > 30
    );
    if (hiredDrivers.length >= 3 && rand() < 0.08 && candidates.length > 0) {
      const sponsor = pickRandom(hiredDrivers, rand);
      // Le candidat ne sera pas encore disponible pris par un concurrent.
      const recommended = pickRandom(candidates.filter(c => !availableIds.includes(c.id)), rand);
      if (recommended && sponsor) {
        availableIds.push(recommended.id);
        events.push({ kind: 'coopted', driverName: recommended.name, sponsorName: sponsor.name });
      }
    }

    // 5. 05/02/2026 — CANDIDATURES SPONTANÉES (annonce site web active).
    // 15 % chance/tick si jobAd active, ajoute 1 profil spécial avec un
    // marqueur "Spontané" affiché dans HR.
    if ((pool.jobAdActive && today < (pool.jobAdEndDay || 0)) && rand() < 0.15 && candidates.length > 0) {
      const sponta = pickRandom(candidates.filter(c => !availableIds.includes(c.id)), rand);
      if (sponta) {
        availableIds.push(sponta.id);
        events.push({ kind: 'spontaneous', driverName: sponta.name });
      }
    }

    // 6. 05/02/2026 — PARTAGE DE CONNAISSANCES (chauffeurs entre eux).
    // 10 % chance/tick si >= 2 chauffeurs hired : un senior partage un peu
    // d'XP avec un junior (les deux niveaux progressent). Effet pur sur
    // la flotte hired, pas sur le vivier — donc on remonte un event narratif.
    const seniors = (state.drivers || []).filter(d => (d.level || 0) >= 3);
    const juniors = (state.drivers || []).filter(d => (d.level || 0) <= 2 && d.status !== 'training');
    if (seniors.length >= 1 && juniors.length >= 1 && rand() < 0.10) {
      const senior = pickRandom(seniors, rand);
      const junior = pickRandom(juniors, rand);
      events.push({ kind: 'knowledge_share', seniorName: senior.name, juniorName: junior.name });
      // Mini-bonus XP : +25 sur le junior (sans changement de level direct).
      const drivers = (state.drivers || []).map(d =>
        d.id === junior.id ? { ...d, xp: (d.xp || 0) + 25 } : d
      );
      // On l'appliquera dans le set() final ci-dessous (override).
      pool._knowledgeShareDrivers = drivers; // marker for the set() below
    }

    // 7. 21/09/2026 — CHASSEUR DE TÊTE : élites niveau 4-5 au fil de l'eau.
    // Seule porte d'entrée récurrente des héros L4-L5 (Lumery, Dubois,
    // Moreau, El Amrani…) dans le vivier. Insertion en tête → visible.
    const hasHeadhunter = !!(state.hiredSupport || {})['role-karim-sefrioui'];
    if (hasHeadhunter && rand() < HEADHUNTER_ELITE_ARRIVAL_PROB) {
      const missingElites = HERO_DRIVERS.filter(d =>
        (d.level || 1) >= HEADHUNTER_ELITE_MIN_LEVEL
        && !availableIds.includes(d.id) && !_rosterIds.has(d.id)
      );
      const elite = pickRandom(missingElites, rand);
      if (elite) {
        availableIds.unshift(elite.id);
        events.push({ kind: 'headhunter_elite', driverName: elite.name });
      }
    }

    const setPayload = {
      driverPool: { ...pool, availableIds, takenBy, lastTickDay: today },
      driverPoolEvents: [...(state.driverPoolEvents || []), ...events].slice(-20),
    };
    if (pool._knowledgeShareDrivers) {
      setPayload.drivers = pool._knowledgeShareDrivers;
      delete pool._knowledgeShareDrivers;
    }
    set(setPayload);

    // 4. Email narratif si arrivées/départs.
    if (events.length > 0 && typeof state.addMessage === 'function') {
      const arrivedCount = events.filter(e => e.kind === 'arrived').length;
      const leftCount = events.filter(e => e.kind === 'left').length;
      const poachedCount = events.filter(e => e.kind === 'poached').length;
      const cooptedEvt = events.find(e => e.kind === 'coopted');
      const spontaEvt = events.find(e => e.kind === 'spontaneous');
      const knowShareEvt = events.find(e => e.kind === 'knowledge_share');
      const eliteEvt = events.find(e => e.kind === 'headhunter_elite');
      const lines = [];
      if (arrivedCount > 0) lines.push(`${arrivedCount} nouveau${arrivedCount > 1 ? 'x' : ''} profil${arrivedCount > 1 ? 's' : ''} dans la base.`);
      if (leftCount > 0) lines.push(`${leftCount} candidat${leftCount > 1 ? 's' : ''} ${leftCount > 1 ? 'sont partis' : 'est parti'} en poste ailleurs.`);
      if (poachedCount > 0) {
        const e = events.find(x => x.kind === 'poached');
        lines.push(`${e.competitor} a recruté ${e.driverName} — il/elle n'est plus disponible.`);
      }
      if (cooptedEvt) {
        lines.push(`💼 **Cooptation** : ${cooptedEvt.sponsorName} (votre équipe) recommande ${cooptedEvt.driverName} — profil intégré au vivier.`);
      }
      if (spontaEvt) {
        lines.push(`📨 **Candidature spontanée** suite à votre annonce site web : ${spontaEvt.driverName}.`);
      }
      if (knowShareEvt) {
        lines.push(`📚 **Partage de connaissances** : ${knowShareEvt.seniorName} a formé ${knowShareEvt.juniorName} (+25 XP).`);
      }
      if (eliteEvt) {
        lines.push(`🎯 **Chasseur de tête** : Karim Sefrioui a fait entrer **${eliteEvt.driverName}** (profil élite) dans le vivier.`);
      }
      state.addMessage({
        kind: 'info',
        sender: 'Équipe RH',
        title: 'Point hebdo recrutement',
        body: lines.join('\n'),
        day: today,
      });
    }
    return true;
  },

  /**
   * Action joueur — déposer une annonce site web.
   * Gratuit, requiert site web actif. Booste le vivier pendant 14 jours.
   */
  postJobAd: () => {
    const state = get();
    if (!state.activeInvestments?.['inv-tech-02']) {
      return { ok: false, reason: 'site_required' };
    }
    if (state.driverPool?.jobAdActive && state.totalDays < state.driverPool.jobAdEndDay) {
      return { ok: false, reason: 'already_active' };
    }
    const today = state.totalDays || 0;
    set({
      driverPool: {
        ...state.driverPool,
        jobAdActive: true,
        jobAdEndDay: today + JOB_AD_DURATION_DAYS,
      },
    });
    if (typeof state.addMessage === 'function') {
      state.addMessage({
        kind: 'info', sender: 'Équipe RH',
        title: 'Annonce site web publiée',
        body: `Annonce active pendant ${JOB_AD_DURATION_DAYS} jours. Candidatures spontanées attendues.`,
        day: today,
      });
    }
    return { ok: true };
  },

  /**
   * Action joueur — organiser un salon RH.
   * Coût 5 000 €, cooldown 30 jours, ajoute 5-8 profils.
   */
  organizeJobFair: () => {
    const state = get();
    const today = state.totalDays || 0;
    if (state.treasury < JOB_FAIR_COST) {
      return { ok: false, reason: 'insufficient_funds' };
    }
    if (today - (state.driverPool?.lastJobFairDay ?? -999) < JOB_FAIR_COOLDOWN_DAYS) {
      return { ok: false, reason: 'cooldown', daysLeft: JOB_FAIR_COOLDOWN_DAYS - (today - state.driverPool.lastJobFairDay) };
    }
    const rand = rng(today * 17 + 3);
    const n = JOB_FAIR_PROFILES_MIN + Math.floor(rand() * (JOB_FAIR_PROFILES_MAX - JOB_FAIR_PROFILES_MIN + 1));
    const pool = state.driverPool || {};
    const availableIds = [...(pool.availableIds || [])];
    const candidates = MARKET_DRIVERS.filter(d => !availableIds.includes(d.id));
    const newcomers = [];
    for (let i = 0; i < n && candidates.length > 0; i++) {
      const idx = Math.floor(rand() * candidates.length);
      const picked = candidates.splice(idx, 1)[0];
      availableIds.push(picked.id);
      newcomers.push(picked.name);
    }
    set({
      treasury: state.treasury - JOB_FAIR_COST,
      driverPool: { ...pool, availableIds, lastJobFairDay: today },
    });
    if (typeof state.addMessage === 'function') {
      state.addMessage({
        kind: 'info', sender: 'Équipe RH',
        title: `Salon RH — ${newcomers.length} profils rencontrés`,
        body: `Coût : ${JOB_FAIR_COST.toLocaleString('fr-FR')} €.\nProfils intégrés au vivier :\n• ${newcomers.join('\n• ')}`,
        day: today,
      });
    }
    return { ok: true, count: newcomers.length };
  },

  /**
   * Action joueur — débaucher un chauffeur pris par un concurrent.
   * Requiert Karim Sefrioui actif. AUCUN frais (22/07/2026 — retour user) :
   * le chauffeur accepte de discuter ou non (35 % de refus, cooldown 45 j),
   * et s'il est ouvert, il attendra une offre salariale attractive (+15 %)
   * lors de la négociation d'embauche.
   */
  poachDriver: (driverId) => {
    const state = get();
    if (!state.hiredSupport?.['role-karim-sefrioui']) {
      return { ok: false, reason: 'headhunter_required' };
    }
    const pool = state.driverPool || {};
    if (!pool.takenBy?.[driverId]) {
      return { ok: false, reason: 'not_taken' };
    }
    const driverData = DRIVERS.find(d => d.id === driverId);
    if (!driverData) return { ok: false, reason: 'unknown_driver' };

    const today = state.totalDays || 0;
    const retryDay = pool.poachCooldowns?.[driverId] || 0;
    if (retryDay > today) {
      return { ok: false, reason: 'cooldown', until: retryDay };
    }

    const competitor = pool.takenBy[driverId];
    const rand = rng(today * 13 + driverId.length);
    if (rand() < POACH_REFUSAL_RATE) {
      // Refus : le chauffeur reste fidèle à son employeur. Aucun frais,
      // mais Karim ne peut pas le relancer avant 45 jours.
      set({
        driverPool: {
          ...pool,
          poachCooldowns: { ...(pool.poachCooldowns || {}), [driverId]: today + POACH_RETRY_COOLDOWN_DAYS },
        },
      });
      return { ok: false, reason: 'refused', retryDay: today + POACH_RETRY_COOLDOWN_DAYS };
    }
    // Succès : le chauffeur est ouvert à la discussion. Il redevient
    // disponible au recrutement, mais attend une offre attractive (+15 %).
    const takenBy = { ...pool.takenBy };
    delete takenBy[driverId];
    const cooldowns = { ...(pool.poachCooldowns || {}) };
    delete cooldowns[driverId];
    set({
      driverPool: { ...pool, takenBy, poachCooldowns: cooldowns },
      driverNegotiations: {
        ...(state.driverNegotiations || {}),
        [driverId]: {
          ...((state.driverNegotiations || {})[driverId] || {}),
          poachPremium: POACH_SALARY_PREMIUM,
          poachedFrom: competitor,
        },
      },
    });
    if (typeof state.addMessage === 'function') {
      state.addMessage({
        kind: 'success', sender: 'Karim Sefrioui',
        title: `Contact établi : ${driverData.name}`,
        body: `${driverData.name} est prêt à quitter ${competitor} — mais il est déjà en poste : compte environ +${Math.round(POACH_SALARY_PREMIUM * 100)} % sur ses prétentions salariales pour le convaincre. Tu peux lui faire une proposition dans l'onglet RH.`,
        day: today,
      });
    }
    return { ok: true, premium: POACH_SALARY_PREMIUM, competitor };
  },
});

export const DRIVER_POOL_CONSTANTS = {
  TICK_INTERVAL_DAYS, BASE_POOL_VISIBLE,
  VISIBLE_BONUS_RH_TIER_1, VISIBLE_BONUS_RH_TIER_2,
  VISIBLE_BONUS_TECH_WEBSITE, VISIBLE_BONUS_JOB_AD,
  JOB_FAIR_COST, JOB_FAIR_COOLDOWN_DAYS,
  JOB_FAIR_PROFILES_MIN, JOB_FAIR_PROFILES_MAX,
  JOB_AD_DURATION_DAYS,
  COMPETITOR_POACH_PROB, MARKET_DEPART_PROB, MARKET_ARRIVAL_PROB,
  HEADHUNTER_ELITE_ARRIVAL_PROB, HEADHUNTER_ELITE_MIN_LEVEL,
  POACH_SALARY_PREMIUM, POACH_REFUSAL_RATE, POACH_RETRY_COOLDOWN_DAYS,
};
